import { useIntervalFn } from '@vueuse/core';

import { useSocketsStore } from '@/stores/sockets';
import { ConnectionEntry, MultiSocketControllerOptions } from '@/types/socket';
import { MULTI_SOCKET_ERROR_MESSAGE_MAP } from '@/utils/constants/multiSocket';
import { getInitialConnectionInfoTemplate } from '@/utils/constants/socket';
import { SocketState } from '@/utils/enums';
import getSocketId from '@/utils/getSocketId';

const RECONNECT_DELAY_MS = 3000;
const RECONNECT_INTERVAL_MS = 1000;

export default function useMultiSocketController(isUsingAutoReconnect: boolean = true) {
  const socketsStore = useSocketsStore();

  const { t } = useI18n();

  const socketId = ref<string>('');
  const errorMessage = ref<string>('');

  const socket = computed<ConnectionEntry>(() => {
    return socketsStore.connections.get(socketId.value) ?? ({} as ConnectionEntry);
  });

  const socketState = computed<SocketState>(() => {
    return socket.value.state;
  });

  const isSocketConnected = computed<boolean>(() => {
    return socketState.value === 'OPEN' && !!socket.value.ws && socket.value.ws.readyState === WebSocket.OPEN;
  });

  const setupSocket = (options: MultiSocketControllerOptions) => {
    socketId.value = options.socketId ? options.socketId : getSocketId();
    socketsStore.setConnectInfo(socketId.value, {
      ...getInitialConnectionInfoTemplate(),
      ...(options.connectionInfo ?? {}),
    });
  };

  const connectSocket = async (isForce = false) => {
    try {
      await socketsStore.setupWebSocket(socketId.value, isForce);
    } catch (e) {
      console.error(e);
    }
  };

  const disconnectSocket = (isForce = false) => {
    socketsStore.close(socketId.value, isForce);
  };

  const handleOnline = async () => {
    await connectSocket(true);
  };

  const handleOffline = () => {
    disconnectSocket(true);
  };

  const handleBeforeUnload = () => {
    disconnectSocket(true);
  };

  onMounted(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeunload', handleBeforeUnload);
  });

  onUnmounted(() => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('beforeunload', handleBeforeUnload);
  });

  const { pause: clearSocketInterval, resume: startSocketInterval } = useIntervalFn(
    async () => {
      if (socketState.value === 'CLOSED') {
        await connectSocket(true);
      } else {
        clearSocketInterval();
      }
    },
    RECONNECT_INTERVAL_MS,
    { immediate: false }
  );

  watch(socketState, (state) => {
    if (state === 'CLOSED' && isUsingAutoReconnect) {
      setTimeout(startSocketInterval, RECONNECT_DELAY_MS);
    }
  });

  watch(
    () => socket.value.connectionResponse,
    (responseCode) => {
      const message = MULTI_SOCKET_ERROR_MESSAGE_MAP[responseCode];
      errorMessage.value = message ? t(message) : '';
      if (errorMessage.value) {
        disconnectSocket();
      }
    },
    { immediate: true }
  );

  return {
    socketState,
    isSocketConnected,
    errorMessage,
    setupSocket,
    connectSocket,
    disconnectSocket,
  };
}
